import React, { useState, useEffect, useRef } from 'react'
import { Container, Card, Row, Col, Dropdown, OverlayTrigger, Tooltip, Button, Modal, Nav, Tab, Badge, ProgressBar } from 'react-bootstrap'
import { Link, useNavigate, Route } from 'react-router-dom'
import { apiHandler } from '../../api/apihandler';
import { STUDENT_TEST_DETAILS_API, TEACHERS_HELPER_ANSWER_SAVE_API, TEACHERS_QUESTIONS_LIST_API } from "../../api/constants";
import Moment from 'moment';
import ShareModal from '../../components/ai/ShareModal';
import { InView } from "react-intersection-observer";
import loader from '../../assets/images/page-img/page-load-loader.gif'
import { useMemo } from 'react';
import { STUDY_CARD_STYLES, StudySectionHeader, StudyEmptyState, toAnswerChips } from './StudyCardKit';

//Sweet alert
import Swal from 'sweetalert2'

// Redux Selector / Action
import { useDispatch } from 'react-redux';

const StudentHomeworkComp = ({ upcomingHWList, pastHWList, setShowMoreDataLoader }) => {

    const navigate = useNavigate();
    const [showExamModal, setShowExamModal] = useState(false);
    const [examSection, setExamSection] = useState(null); // "MCQ", "SAQ", "LAQ"
    const [examQuestions, setExamQuestions] = useState([]);
    const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
    const [userAnswers, setUserAnswers] = useState({});
    const [examSubmitted, setExamSubmitted] = useState(false);
    const [studentTestDetails, setStudentTestDetails] = useState('');
    const [showResultModal, setShowResultModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [timeLeftSec, setTimeLeftSec] = useState(null); // null = untimed homework
    const autoSubmittedRef = useRef(false);

    useEffect(() => {
        if (studentTestDetails && studentTestDetails.questions) {
            setShowResultModal(true);
        }
    }, [studentTestDetails]);

    // Start (and clean up) the countdown whenever a homework session opens.
    useEffect(() => {
        if (!showExamModal) return undefined;
        autoSubmittedRef.current = false;
        const durationMin = Number(selectedTest?.test_details?.test_duration);
        if (!examQuestions.length || !durationMin || durationMin <= 0) {
            setTimeLeftSec(null);
            return undefined;
        }
        setTimeLeftSec(durationMin * 60);
        const timerId = setInterval(() => {
            setTimeLeftSec(prev => (prev === null || prev <= 1 ? 0 : prev - 1));
        }, 1000);
        return () => clearInterval(timerId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showExamModal]);

    // Warn before the tab/window closes while a homework attempt is in progress.
    useEffect(() => {
        if (!showExamModal || examSubmitted) return undefined;
        const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [showExamModal, examSubmitted]);

    const groupTestsByDate = (searches) => {
        if (!Array.isArray(searches)) return {};
        const grouped = {};
        searches.forEach(test => {
            // Count total questions
            const q = test.questions || {};
            const totalQuestions =
                (q.mcqs?.length || 0) +
                (q.saqs?.length || 0) +
                (q.laqs?.length || 0) +
                (q.nonWeighted?.length || 0);

            // Only include tests with at least 1 question
            if (totalQuestions > 0) {
                const date = test?.test_details?.test_date
                    ? Moment(test.test_details.test_date).format('YYYY-MM-DD')
                    : 'No Date';
                if (!grouped[date]) grouped[date] = [];
                grouped[date].push(test);
            }
        });
        return grouped;
    };

    const groupedTests = useMemo(() => groupTestsByDate(upcomingHWList), [upcomingHWList]);
    const [selectedTest, setSelectedTest] = useState(null);

    // Handle answer change
    const handleAnswerChange = (qid, value) => {
        if (examSubmitted || isSaving) return;
        setUserAnswers(prev => ({ ...prev, [qid]: value }));
    };

    const isQuestionAnswered = (q) => {
        const val = userAnswers[q?.id];
        return typeof val === 'string' ? val.trim() !== '' : Boolean(val);
    };

    const formatTime = (totalSeconds) => {
        const s = Math.max(0, Number(totalSeconds) || 0);
        const hrs = Math.floor(s / 3600);
        const mins = Math.floor((s % 3600) / 60);
        const secs = s % 60;
        const pad = (n) => String(n).padStart(2, '0');
        return hrs > 0 ? `${pad(hrs)}:${pad(mins)}:${pad(secs)}` : `${pad(mins)}:${pad(secs)}`;
    };

    const notifyError = (text) => {
        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'error',
            title: text,
            showConfirmButton: false,
            timer: 2800,
            timerProgressBar: true,
        });
    };

    // Turns whatever is currently in userAnswers for a question into the
    // (studentAnswer, optionId) pair the save API expects. MCQ answers are
    // stored as an "A."-style label locally but the backend wants the full
    // option text plus that option's id.
    const buildAnswerPayload = (q) => {
        const selectedLabel = userAnswers[q?.id];
        if (!q || q.type !== "mcq") {
            return { studentAnswer: selectedLabel || "", optionId: null };
        }
        let selectedText = "";
        if (selectedLabel && q.options) {
            const opts = Array.isArray(q.options) ? q.options : JSON.parse(q.options);
            const idx = selectedLabel.charCodeAt(0) - 65;
            if (opts[idx]) selectedText = opts[idx];
        }
        let optionId = null;
        if (selectedText && Array.isArray(q.mcq_options)) {
            const match = q.mcq_options.find(opt => opt.option_text === selectedText);
            optionId = match?.id ?? null;
        }
        return { studentAnswer: selectedText || selectedLabel || "", optionId };
    };

    // Saves whatever answer is currently on screen for the given question.
    // Used for auto-save on Next/Previous/jump, and for the final submit.
    const persistAnswer = async (q, isSubmit) => {
        if (!q) return true;
        const { studentAnswer, optionId } = buildAnswerPayload(q);
        return saveAnswerApi(q.id, studentAnswer, optionId, isSubmit);
    };

    // Jump straight to any question via the navigator, auto-saving the one
    // being left first so an edit is never silently dropped.
    const goToQuestion = async (idx) => {
        if (isSaving || examSubmitted || idx === currentQuestionIdx) return;
        if (idx < 0 || idx >= examQuestions.length) return;
        await persistAnswer(examQuestions[currentQuestionIdx], false);
        setCurrentQuestionIdx(idx);
    };

    // Next/Prev navigation — both auto-save the question being left so
    // going back and forth never loses an answer.
    const handleNext = async () => {
        if (isSaving || examSubmitted) return;
        await persistAnswer(examQuestions[currentQuestionIdx], false);
        setCurrentQuestionIdx(idx => Math.min(idx + 1, examQuestions.length - 1));
    };

    const handlePrev = async () => {
        if (isSaving || examSubmitted || currentQuestionIdx === 0) return;
        await persistAnswer(examQuestions[currentQuestionIdx], false);
        setCurrentQuestionIdx(idx => Math.max(idx - 1, 0));
    };

    // Submit flow: confirm first (warning about unanswered questions if
    // any), then save the final answer with is_submitted=1. Scoring is the
    // teacher's job (they publish results separately) — the student side
    // just needs to record the submission and confirm it went through.
    // `auto` is used by the exam-timer's time's-up handler, which must not
    // block on a confirmation dialog that has nobody left to answer it.
    const submitExam = async ({ auto = false } = {}) => {
        if (isSaving || examSubmitted || !examQuestions.length) return;

        if (!auto) {
            const unansweredCount = examQuestions.filter(q => !isQuestionAnswered(q)).length;
            const confirmResult = await Swal.fire({
                title: unansweredCount > 0 ? 'Unanswered questions' : 'Submit homework?',
                html: unansweredCount > 0
                    ? `You have <b>${unansweredCount}</b> unanswered question${unansweredCount > 1 ? 's' : ''}. Once submitted, you can't change your answers.`
                    : `Once submitted, you can't change your answers. Ready to submit?`,
                icon: unansweredCount > 0 ? 'warning' : 'question',
                showCancelButton: true,
                confirmButtonText: unansweredCount > 0 ? 'Submit Anyway' : 'Yes, Submit',
                cancelButtonText: 'Review Again',
                confirmButtonColor: '#28a745',
                reverseButtons: true,
            });
            if (!confirmResult.isConfirmed) return;
        }

        const saved = await persistAnswer(examQuestions[currentQuestionIdx], true);
        if (saved) {
            setExamSubmitted(true);
            setShowExamModal(false);
            Swal.fire({
                title: 'Success',
                text: 'Your homework has been submitted successfully.',
                icon: 'success',
                confirmButtonText: 'Okay'
            });
        }
    };

    const handleSubmitExam = () => submitExam({ auto: false });

    async function saveAnswerApi(qId, studentAnswer, optionId, isSubmit) {
        setIsSaving(true);
        setShowMoreDataLoader(true)
        const params = {
            question_id: qId,
            student_answer: studentAnswer,
            option_ids: (optionId !== "" && optionId !== null && optionId !== undefined) ? optionId : {},
            is_submitted: isSubmit ? 1 : 0,
        }
        try {
            const res = await apiHandler(TEACHERS_HELPER_ANSWER_SAVE_API, "POST", params)
            if (res.data?.response?.status === 200) {
                return true;
            }
            notifyError("Couldn't save that answer. Please check your connection.");
            return false;
        } catch (error) {
            console.error(error);
            notifyError("Couldn't save that answer. Please check your connection.");
            return false;
        }
        finally {
            setIsSaving(false)
            setShowMoreDataLoader(false)
        }
    }

    // Auto-submit the instant the countdown hits zero.
    useEffect(() => {
        if (timeLeftSec !== 0 || !showExamModal || examSubmitted || autoSubmittedRef.current) return;
        autoSubmittedRef.current = true;
        Swal.fire({
            icon: 'info',
            title: "Time's up!",
            text: "Your homework is being submitted automatically.",
            timer: 2200,
            showConfirmButton: false,
        });
        submitExam({ auto: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeLeftSec, showExamModal, examSubmitted]);

    // Confirms before discarding an in-progress attempt when the student
    // clicks the modal's close button.
    const handleCloseExamModal = async () => {
        if (examSubmitted || !examQuestions.length) {
            setShowExamModal(false);
            return;
        }
        const result = await Swal.fire({
            title: 'Exit homework?',
            text: 'Answers you already moved past are saved, but you will need to reopen this homework to finish it.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Exit',
            cancelButtonText: 'Stay',
            confirmButtonColor: '#dc3545',
            reverseButtons: true,
        });
        if (result.isConfirmed) {
            setShowExamModal(false);
        }
    };

    async function getStudentTestDetailsApi(aiSearchId) {
        setShowMoreDataLoader(true)
        try {
            const res = await apiHandler(STUDENT_TEST_DETAILS_API, "POST", {
                ai_search_id: aiSearchId,
            })
            if (res.data?.response?.status === 200) {
                setStudentTestDetails(res.data?.data?.test_details || '');
            } else {
                notifyError("Couldn't load the results. Please try again.");
            }
        } catch (error) {
            console.error(error);
            notifyError("Couldn't load the results. Please try again.");
        }
        finally {
            setShowMoreDataLoader(false)
        }
    }

    const getPastHomeworkStatus = (test) => {
        if (test.test_details?.is_result_published === 1) {
            return { text: "Graded", icon: "check_circle", pillClass: "bg-soft-success" };
        }
        if (test.test_details?.is_submitted === 1) {
            return { text: "Pending Review", icon: "hourglass_top", pillClass: "bg-soft-warning" };
        }
        return { text: "Not Submitted", icon: "event_busy", pillClass: "bg-soft-secondary" };
    };

    const renderPastTestListByDate = () => (
        <div className="study-card-grid">
            {pastHWList.length === 0 && (
                <StudyEmptyState
                    icon="history_edu"
                    tone="secondary"
                    title="No past homework yet"
                    subtitle="Completed homework will show up here."
                />
            )}
            {pastHWList.map((test, idx) => {
                const status = getPastHomeworkStatus(test);
                const testDate = test.test_details?.test_date;
                return (
                    <div className="card study-card study-card-past border-0" key={test.ai_search_id ?? idx}>
                        <div className="card-body study-card-body d-flex flex-column">
                            <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="study-card-icon bg-soft-secondary">
                                        <i className="material-symbols-outlined">history_edu</i>
                                    </div>
                                    <div>
                                        <div className="study-card-eyebrow text-secondary">Past Homework</div>
                                        <h6 className="study-card-title mb-0">{test.topic}</h6>
                                    </div>
                                </div>
                            </div>
                            <div className="study-card-meta">
                                {testDate && (
                                    <span>
                                        <i className="material-symbols-outlined">calendar_today</i>
                                        {Moment(testDate).format('MMM D, YYYY')}
                                        <span className="text-muted"> · {Moment(testDate).fromNow()}</span>
                                    </span>
                                )}
                                {test.test_details?.test_duration && (
                                    <span>
                                        <i className="material-symbols-outlined">schedule</i>
                                        {test.test_details.test_duration} min
                                    </span>
                                )}
                            </div>
                            <div className={`study-status-pill align-self-start ${status.pillClass}`}>
                                <i className="material-symbols-outlined">{status.icon}</i>
                                {status.text}
                            </div>
                            <div className="mt-auto pt-3 border-top d-flex align-items-center justify-content-between">
                                {test.test_details?.exam_score ? (
                                    <div className="study-card-score">
                                        <i className="material-symbols-outlined">stars</i>
                                        <strong>{test.test_details.exam_score}</strong>&nbsp;marks
                                    </div>
                                ) : <span />}
                                {test.test_details?.is_result_published === 1 ? (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="rounded-pill px-3"
                                        onMouseDown={e => e.preventDefault()}
                                        onClick={() => {
                                            setSelectedTest(test);
                                            getStudentTestDetailsApi(test?.ai_search_id)
                                        }}
                                    >
                                        <i className="material-symbols-outlined align-middle me-1" style={{ fontSize: 16 }}>visibility</i>
                                        View Results
                                    </Button>
                                ) : (
                                    <span className="small text-muted fst-italic">
                                        {test.test_details?.is_submitted === 1 ? "Result not published yet" : "You did not submit this"}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const getUpcomingHomeworkMeta = (test) => {
        const isToday = Boolean(test.test_details?.test_date &&
            Moment(test.test_details.test_date).format('YYYY-MM-DD') === Moment().format('YYYY-MM-DD'));
        // is_submitted comes back from the API as a number (1/0), same as
        // everywhere else in this file (see getPastHomeworkStatus above) -
        // this was comparing it to the boolean `false`, which a numeric 0
        // never strictly equals, so canStart was always false and "Start
        // Homework" never appeared even on homework due today.
        const isSubmitted = test?.test_details?.is_submitted === true || Number(test?.test_details?.is_submitted) === 1;
        const canStart = isToday && !isSubmitted;
        return { isToday, canStart };
    };

    // A friendly, Classroom-style relative label: "Today", "Tomorrow",
    // "Monday" if within the coming week, else a plain date.
    const formatSmartDate = (date) => Moment(date).calendar(null, {
        sameDay: '[Today]',
        nextDay: '[Tomorrow]',
        nextWeek: 'dddd',
        lastDay: '[Yesterday]',
        lastWeek: '[Last] dddd',
        sameElse: 'MMM D, YYYY',
    });

    const QUESTION_TYPE_STYLES = {
        MCQ: "bg-soft-primary",
        SAQ: "bg-soft-success",
        LAQ: "bg-soft-warning",
        Other: "bg-soft-info",
    };

    const renderQuestionTypeChips = (questions) => {
        const chips = [
            ["MCQ", questions?.mcqs?.length],
            ["SAQ", questions?.saqs?.length],
            ["LAQ", questions?.laqs?.length],
            ["Other", questions?.nonWeighted?.length],
        ].filter(([, count]) => count > 0);
        if (!chips.length) return null;
        return (
            <div className="d-flex flex-wrap gap-1 mb-3">
                {chips.map(([label, count]) => (
                    <span key={label} className={`badge rounded-pill fw-semibold ${QUESTION_TYPE_STYLES[label]}`}>
                        {count} {label}
                    </span>
                ))}
            </div>
        );
    };

    // Render test list by date
    const renderTestListByDate = () => (
        <div className="study-card-grid">
            {Object.keys(groupedTests).length === 0 && (
                <StudyEmptyState
                    icon="assignment"
                    tone="primary"
                    title="No homework right now"
                    subtitle="You're all caught up — new homework will appear here."
                />
            )}
            {Object.entries(groupedTests).map(([date, tests]) => (
                <React.Fragment key={date}>
                    {tests.map(test => {
                        const { isToday, canStart } = getUpcomingHomeworkMeta(test);
                        return (
                            <div
                                className={`card study-card study-card-upcoming border-0 ${isToday ? 'study-card-today' : ''}`}
                                key={test.ai_search_id}
                            >
                                <div className="card-body study-card-body d-flex flex-column">
                                    <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="study-card-icon bg-soft-primary">
                                                <i className="material-symbols-outlined">menu_book</i>
                                            </div>
                                            <div>
                                                <div className="study-card-eyebrow text-primary">Homework</div>
                                                <h6 className="study-card-title mb-0">{test.topic}</h6>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="study-card-meta">
                                        {test.test_details?.test_date && (
                                            <span>
                                                <i className="material-symbols-outlined">calendar_today</i>
                                                {formatSmartDate(test.test_details.test_date)}
                                            </span>
                                        )}
                                        {test.test_details?.test_duration && (
                                            <span>
                                                <i className="material-symbols-outlined">schedule</i>
                                                {test.test_details.test_duration} min
                                            </span>
                                        )}
                                        {test.test_details?.exam_score && (
                                            <span>
                                                <i className="material-symbols-outlined">stars</i>
                                                {test.test_details.exam_score} marks
                                            </span>
                                        )}
                                    </div>
                                    {isToday && (
                                        <div className="study-status-pill align-self-start bg-success text-white mb-2">
                                            <i className="material-symbols-outlined">bolt</i>
                                            Due Today
                                        </div>
                                    )}
                                    {renderQuestionTypeChips(test.questions)}
                                    <div className="mt-auto pt-1">
                                        {canStart ? (
                                            <Button
                                                variant="success"
                                                className="study-cta-start rounded-pill w-100 fw-semibold"
                                                onClick={() => {
                                                    setSelectedTest(test);
                                                    handleStartExamSection(test);
                                                }}
                                            >
                                                <i className="material-symbols-outlined align-middle me-1" style={{ fontSize: 18 }}>play_circle</i>
                                                Start Homework
                                            </Button>
                                        ) : (
                                            <div className="text-center small text-muted border-top pt-2">
                                                <i className="material-symbols-outlined align-middle me-1" style={{ fontSize: 15 }}>event_upcoming</i>
                                                Scheduled — opens on the day of the homework
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </React.Fragment>
            ))}
        </div>
    );

    // Update handleStartExamSection to accept selectedTest
    const handleStartExamSection = (testObj = null) => {
        const test = testObj || selectedTest;
        if (!test) return;

        let arr = [];
        const questions = test.questions || {};

        // Helper to map MCQ options
        const mapMcqOptions = (mcq) => ({
            ...mcq,
            options: mcq.mcq_options ? mcq.mcq_options.map(opt => opt.option_text) : [],
            section: "MCQ"
        });

        // Helper for SAQ/LAQ/nonWeighted
        const mapOther = (q, section) => ({
            ...q,
            options: null,
            section
        });

        // Combine all question types into one array
        if (Array.isArray(questions.mcqs)) {
            arr = arr.concat(questions.mcqs.map(mapMcqOptions));
        }
        if (Array.isArray(questions.saqs)) {
            arr = arr.concat(questions.saqs.map(q => mapOther(q, "SAQ")));
        }
        if (Array.isArray(questions.laqs)) {
            arr = arr.concat(questions.laqs.map(q => mapOther(q, "LAQ")));
        }
        if (Array.isArray(questions.nonWeighted)) {
            arr = arr.concat(questions.nonWeighted.map(q => mapOther(q, "nonWeighted")));
        }

        if (!arr.length) {
            notifyError("This homework doesn't have any questions yet.");
            return;
        }

        setExamSection("Homework");
        setExamQuestions(arr);
        setCurrentQuestionIdx(0);
        setUserAnswers({});
        setExamSubmitted(false);
        setIsSaving(false);
        setShowExamModal(true);
    };

    const sectionColor = (section) => section === "MCQ" ? "primary" : section === "SAQ" ? "success" : section === "LAQ" ? "warning" : "secondary";

    // Render question UI
    const renderExamQuestion = () => {
        if (!examQuestions.length) return (
            <div className="text-center py-5 text-muted">
                <i className="material-symbols-outlined" style={{ fontSize: 48 }}>quiz</i>
                <div>No homework available for this section.</div>
            </div>
        );
        const q = examQuestions[currentQuestionIdx];
        const answeredCount = examQuestions.filter(isQuestionAnswered).length;
        const isLocked = examSubmitted || isSaving;
        return (
            <div className="p-2 p-md-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <Badge bg={sectionColor(q.section)} className="fs-6 px-3 py-2 text-uppercase shadow-sm">
                        {q.section}
                    </Badge>
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small">{answeredCount}/{examQuestions.length} answered</span>
                        {timeLeftSec !== null && (
                            <span className={`badge rounded-pill fs-6 px-3 py-2 ${timeLeftSec <= 60 ? 'bg-soft-danger exam-timer-pulse' : timeLeftSec <= 300 ? 'bg-soft-warning' : 'bg-soft-primary'}`}>
                                <i className="material-symbols-outlined align-middle me-1" style={{ fontSize: 16 }}>timer</i>
                                {formatTime(timeLeftSec)}
                            </span>
                        )}
                    </div>
                </div>

                <div className="d-flex flex-wrap gap-2 mb-3">
                    {examQuestions.map((qq, i) => {
                        const answered = isQuestionAnswered(qq);
                        const isCurrent = i === currentQuestionIdx;
                        return (
                            <button
                                key={qq.id ?? i}
                                type="button"
                                className={`exam-nav-dot ${isCurrent ? 'exam-nav-dot-current' : ''} ${answered ? 'exam-nav-dot-answered' : ''}`}
                                onClick={() => goToQuestion(i)}
                                disabled={isLocked}
                                title={`Question ${i + 1}${answered ? ' — answered' : ''}`}
                            >
                                {i + 1}
                            </button>
                        );
                    })}
                </div>

                <ProgressBar
                    now={((currentQuestionIdx + 1) / examQuestions.length) * 100}
                    className="mb-4"
                    style={{ height: 8, borderRadius: 8 }}
                    variant={sectionColor(q.section)}
                />
                <div className="mb-4 p-3 rounded bg-light border shadow-sm fw-semibold text-primary" style={{ fontSize: 20 }}>
                    {q.question_text}
                </div>
                {q.section === "MCQ" && q.options && (
                    <div className="mb-4">
                        {q.options.map((opt, i) => {
                            const label = String.fromCharCode(65 + i) + ".";
                            const checked = userAnswers[q.id] === label;
                            return (
                                <label
                                    key={i}
                                    htmlFor={`opt_${q.id}_${i}`}
                                    className={`exam-option ${checked ? 'exam-option-selected bg-soft-primary' : ''}`}
                                    style={{ cursor: isLocked ? "not-allowed" : "pointer" }}
                                >
                                    <input
                                        className="form-check-input me-2"
                                        type="radio"
                                        name={`answer_${q.id}`}
                                        id={`opt_${q.id}_${i}`}
                                        value={label}
                                        checked={checked}
                                        onChange={() => handleAnswerChange(q.id, label)}
                                        disabled={isLocked}
                                        style={{ cursor: isLocked ? "not-allowed" : "pointer" }}
                                    />
                                    <span className="option-label fw-bold me-1">{label}</span> {opt}
                                </label>
                            );
                        })}
                    </div>
                )}
                {q.section !== "MCQ" && (
                    <div className="mb-4">
                        <textarea
                            className="form-control shadow-sm"
                            rows={4}
                            placeholder="Type your answer..."
                            value={userAnswers[q.id] || ""}
                            onChange={e => handleAnswerChange(q.id, e.target.value)}
                            disabled={isLocked}
                            style={{ fontSize: 16 }}
                        />
                    </div>
                )}
                <div className="d-flex justify-content-between align-items-center mt-4">
                    <Button
                        variant="outline-secondary"
                        disabled={currentQuestionIdx === 0 || isLocked}
                        onClick={handlePrev}
                    >
                        <i className="material-symbols-outlined align-middle me-1">arrow_back</i> Previous
                    </Button>
                    {currentQuestionIdx < examQuestions.length - 1 ? (
                        <Button
                            variant="primary"
                            onClick={handleNext}
                            disabled={isLocked}
                        >
                            {isSaving ? 'Saving…' : <>Next <i className="material-symbols-outlined align-middle ms-1">arrow_forward</i></>}
                        </Button>
                    ) : (
                        <Button
                            variant="success"
                            onClick={handleSubmitExam}
                            disabled={isLocked}
                        >
                            {examSubmitted ? (
                                <span><i className="material-symbols-outlined align-middle me-1">check_circle</i> Submitted</span>
                            ) : isSaving ? 'Submitting…' : (
                                <span><i className="material-symbols-outlined align-middle me-1">send</i> Submit</span>
                            )}
                        </Button>
                    )}
                </div>
                {examSubmitted && (
                    <div className="alert alert-success mt-4 text-center">
                        <i className="material-symbols-outlined align-middle me-2">check_circle</i>
                        Homework submitted! Thank you for your answers.
                    </div>
                )}
            </div>
        );
    };

    const renderStudentTestResult = () => {
        if (!studentTestDetails || !studentTestDetails.questions) return null;
        return (
            <div>
                <div className="mb-3 text-center">
                    <h5 className="fw-bold text-primary">{studentTestDetails.topic}</h5>
                    <div>
                        <Badge bg="info" className="me-2">{Moment(studentTestDetails.test_date).format('MMM D YYYY')}</Badge>
                        <Badge bg="success" className="me-2">Score: {studentTestDetails.obtained_score} / {studentTestDetails.exam_score}</Badge>
                    </div>
                </div>
                {studentTestDetails.questions.map((q, idx) => (
                    <div key={q.question_id} className="question-card mb-3 p-3 rounded shadow-sm bg-light">
                        <div className="fw-bold mb-2 text-primary">
                            Q{idx + 1}: {q.question_text}
                        </div>
                        <div className="mb-1">
                            <span className="badge bg-secondary me-2">Type: {(q.type || '').toUpperCase()}</span>
                            <span className={`badge ${q.is_correct ? "bg-success" : "bg-danger"}`}>
                                {q.is_correct ? "Correct" : "Incorrect"}
                            </span>
                            <span className="badge bg-info ms-2">Score: {q.score}</span>
                        </div>
                        <div className="mt-2">
                            <div>
                                <span className="fw-semibold text-muted">Correct Answer:</span>
                                {toAnswerChips(q.correct_answer).map((ans) => (
                                    <span key={ans.id} className="ms-2 badge bg-success">{ans.text}</span>
                                ))}
                            </div>
                            <div className="mt-1">
                                <span className="fw-semibold text-muted">Your Answer:</span>
                                {toAnswerChips(q.student_answer).map((ans) => (
                                    <span key={ans.id} className="ms-2 badge bg-primary">{ans.text}</span>
                                ))}
                            </div>
                        </div>
                        {q.feedback && (
                            <div className="mt-2 alert alert-info p-2">
                                <i className="material-symbols-outlined align-middle me-1">info</i>
                                {q.feedback}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    };


    return (
        <>
            <style>{STUDY_CARD_STYLES}</style>
        <Row>
            <Col lg="6" className="mb-4 mb-lg-0">
                <Card className="h-100">
                    <Card.Header className="d-flex align-items-center justify-content-between">
                        <StudySectionHeader icon="history_edu" tone="secondary" title="Past Homework" count={pastHWList.length} />
                    </Card.Header>
                    <Card.Body>
                        {renderPastTestListByDate()}
                    </Card.Body>
                </Card>
            </Col>
            <Col lg="6">
                <Card className="h-100">
                    <Card.Header className="d-flex align-items-center justify-content-between">
                        <StudySectionHeader
                            icon="menu_book"
                            tone="primary"
                            title="Upcoming Homework"
                            count={Object.values(groupedTests).reduce((sum, t) => sum + t.length, 0)}
                        />
                    </Card.Header>
                    <Card.Body>
                        {renderTestListByDate()}

                        <Modal
                            show={showExamModal}
                            onHide={handleCloseExamModal}
                            centered
                            size="lg"
                            backdrop="static"
                            keyboard={false}
                            enforceFocus={false}
                            contentClassName="rounded-4"
                        >
                            <Modal.Header closeButton className="rounded-top-4">
                                <Modal.Title>
                                    <span className="fw-bold text-primary">{examSection ? `${examSection}` : "Homework"}</span>
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body className="rounded-bottom-4">
                                {renderExamQuestion()}
                            </Modal.Body>
                        </Modal>

                        <Modal
                            show={showResultModal}
                            onHide={() => { setShowResultModal(false); setStudentTestDetails(''); }}
                            centered
                            size="lg"
                            backdrop="static"
                            keyboard={false}
                            contentClassName="rounded-4"
                            dialogClassName="responsive-modal"
                        >
                            <Modal.Header closeButton className="rounded-top-4">
                                <Modal.Title>
                                    <span className="fw-bold text-primary">Homework Results</span>
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body className="rounded-bottom-4" style={{ maxHeight: "70vh", overflowY: "auto", paddingBottom: 24 }}>
                                {renderStudentTestResult()}
                            </Modal.Body>
                        </Modal>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
        </>
    )
}

export default StudentHomeworkComp